import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, Link, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCorners,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Archive,
  Check,
  CheckSquare,
  Clock,
  GripVertical,
  History,
  Kanban,
  MessageSquare,
  Paperclip,
  Plus,
  Settings,
  Settings2,
  Table2,
  X,
} from "lucide-react";
import { CARD_TITLE_MAX_LENGTH, type BoardField, type LabelColor } from "@kandoo/shared";
import { api, type BoardDetail, type BoardCard } from "../api/client";
import { sortCards, sortLists } from "../util/position";
import { EditableText } from "../components/EditableText";
import { ConfirmButton } from "../components/ConfirmButton";
import { OverflowMenu } from "../components/OverflowMenu";
import { ColorSwatches } from "../components/ColorSwatches";
import { toast } from "../components/Toaster";
import { clipboardFiles, useFileDrop } from "../hooks/useFileDrop";
import { LabelChip } from "../components/LabelChip";
import { Avatar } from "../components/Avatar";
import { ShareBoard } from "../components/ShareBoard";
import { BoardSettings } from "../components/BoardSettings";
import { ArchivedCards } from "../components/ArchivedCards";
import { BoardActivity } from "../components/BoardActivity";
import { BoardSwitcher } from "../components/BoardSwitcher";
import { MoveListCardsDialog } from "../components/MoveListCardsDialog";
import { BoardFieldsDialog } from "../components/BoardFieldsDialog";
import { TableView } from "../components/TableView";
import { formatFieldValue } from "../util/fields";
import { LabelPicker } from "../components/LabelPicker";
import { formatDueShort, isDueSoon, isOverdue } from "../util/date";
import { labelColor } from "../util/labelColors";
import { backgroundStyle } from "../util/backgrounds";
import { useMe } from "../hooks/useMe";
import {
  BoardFilters,
  cardPassesFilters,
  filtersActive,
  type BoardFilters as BoardFiltersState,
  type DueFilter,
} from "../components/BoardFilters";

/** Droppable id prefix for a list's card container (kept distinct from the
 *  list's own sortable id, which equals the list id). */
const CONTAINER = "container:";

/** Fractional position between two neighbors (null = open end). */
function midpoint(prev: number | null, next: number | null): number {
  if (prev == null && next == null) return 0;
  if (prev == null) return (next as number) - 1;
  if (next == null) return prev + 1;
  return (prev + next) / 2;
}

type BoardView = "board" | "table";
const VIEW_KEY = (boardId: string) => `kandoo:view:${boardId}`;
function readView(boardId: string): BoardView {
  try {
    return localStorage.getItem(VIEW_KEY(boardId)) === "table" ? "table" : "board";
  } catch {
    return "board";
  }
}
function writeView(boardId: string, view: BoardView) {
  try {
    if (view === "board") localStorage.removeItem(VIEW_KEY(boardId));
    else localStorage.setItem(VIEW_KEY(boardId), view);
  } catch {
    /* private mode etc. — the URL still carries it */
  }
}

/** URL params ⇄ search/filter/view state. Empty values are left out. */
function filtersFromParams(p: URLSearchParams): BoardFiltersState {
  const list = (k: string) => p.get(k)?.split(",").filter(Boolean) ?? [];
  return {
    assignedToMe: p.get("mine") === "1",
    labelIds: list("labels"),
    assigneeIds: list("assignees"),
    due: list("due").filter((d): d is DueFilter => d === "overdue" || d === "due-soon"),
    fieldValues: p.getAll("field"),
  };
}
function paramsFromState(q: string, f: BoardFiltersState, view: BoardView): URLSearchParams {
  const p = new URLSearchParams();
  if (q) p.set("q", q);
  if (f.assignedToMe) p.set("mine", "1");
  if (f.labelIds.length) p.set("labels", f.labelIds.join(","));
  if (f.assigneeIds.length) p.set("assignees", f.assigneeIds.join(","));
  if (f.due.length) p.set("due", f.due.join(","));
  for (const fv of f.fieldValues) p.append("field", fv);
  if (view === "table") p.set("view", "table");
  return p;
}

type CardSort = "due" | "title" | "newest";
const CARD_SORTS: Record<CardSort, { label: string; compare: (a: BoardCard, b: BoardCard) => number }> = {
  // no due date sorts last
  due: {
    label: "Due date",
    compare: (a, b) =>
      !a.dueDate || !b.dueDate
        ? Number(!a.dueDate) - Number(!b.dueDate)
        : Date.parse(a.dueDate) - Date.parse(b.dueDate),
  },
  title: {
    label: "Title",
    compare: (a, b) => a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: "base" }),
  },
  newest: { label: "Newest first", compare: (a, b) => b.createdAt.localeCompare(a.createdAt) },
};

/** Is focus somewhere the user types, so a bare letter key is text, not a shortcut? */
function isTypingTarget(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable || el.tagName === "TEXTAREA" || el.tagName === "SELECT")
    return true;
  return (
    el.tagName === "INPUT" &&
    !["button", "checkbox", "radio", "range", "color", "file", "submit", "reset"].includes(
      (el as HTMLInputElement).type,
    )
  );
}

const PICKER_WIDTH = 288; // w-72
const PICKER_MAX_HEIGHT = 420;

/**
 * Does a card match the search query? Matches title, description, labels, and
 * assignees, plus the card's comment text when it's been loaded (the comment
 * index is fetched lazily, so it may be undefined on the first keystroke).
 */
function cardMatches(card: BoardCard, q: string, commentText?: string): boolean {
  const hay = [
    card.title,
    card.description ?? "",
    commentText ?? "",
    ...card.labels.map((l) => l.name ?? ""),
    ...card.assignees.map((a) => `${a.displayName ?? ""} ${a.username}`),
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

/** The board's fields flagged "show on card faces", in order, for CardFace. */
const CardFaceFieldsContext = createContext<BoardField[]>([]);

/* ---- per-user collapsed-list persistence (localStorage, keyed by board) ---- */
const collapsedKey = (boardId: string) => `kandoo.collapsed.${boardId}`;

function readCollapsed(boardId: string): string[] {
  try {
    const raw = localStorage.getItem(collapsedKey(boardId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function BoardPage() {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const boardKey = useMemo(() => ["board", boardId] as const, [boardId]);

  /* ---- optimistic drag ordering ---- */
  const [listOrder, setListOrder] = useState<string[]>([]);
  const [cardsByList, setCardsByList] = useState<Record<string, string[]>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<"card" | "list" | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  // search, filters and view live in the URL so a filtered view can be
  // bookmarked or shared, and survive opening a card or a reload
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  // Comment text is fetched only once the user engages the search box, so a
  // comment-heavy board never pays for it on the normal load. Once true it
  // stays true (the fetch is cached), so clearing the box doesn't drop it.
  const [searchEngaged, setSearchEngaged] = useState(false);
  const [filters, setFilters] = useState<BoardFiltersState>(() =>
    filtersFromParams(searchParams),
  );
  // Board or Table; the URL wins, else the last view used on this board
  const [view, setView] = useState<BoardView>(() => {
    const v = searchParams.get("view");
    return v === "table" || v === "board" ? v : readView(boardId ?? "");
  });
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    writeView(boardId ?? "", view);
  }, [boardId, view]);
  // mirror state into the URL (replace, so typing doesn't pile up history);
  // re-run on path changes so opening or closing a card keeps the params
  useEffect(() => {
    const next = paramsFromState(query, filters, view);
    if (next.toString() !== new URLSearchParams(location.search).toString())
      setSearchParams(next, { replace: true });
  }, [query, filters, view, location.pathname, location.search, setSearchParams]);
  // "/" jumps to search, like most web apps
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(document.activeElement) || document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      setSearchEngaged(true);
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // list ids collapsed to a slim bar; persisted per user in localStorage
  const [collapsed, setCollapsed] = useState<string[]>(() =>
    readCollapsed(boardId ?? ""),
  );
  const { me } = useMe();
  // cardId → comment text for search; only fetched after the box is engaged.
  const commentIndex = useQuery({
    queryKey: ["board-comment-index", boardId],
    queryFn: () => api.boards.commentIndex(boardId!),
    enabled: searchEngaged && !!boardId,
    staleTime: 30_000,
  }).data;
  // ref (not state) so the board-sync effect can consult it without re-running
  const draggingRef = useRef(false);
  // the label picker opened from the board with L, pinned under its card
  const [labelPicker, setLabelPicker] = useState<{
    cardId: string;
    top: number;
    left: number;
  } | null>(null);
  const labelPickerRef = useRef<HTMLDivElement | null>(null);

  const {
    data: board,
    isLoading,
    dataUpdatedAt,
    errorUpdatedAt,
  } = useQuery({
    queryKey: boardKey,
    queryFn: () => api.boards.get(boardId!),
    enabled: !!boardId,
    // poll so collaborators' changes appear without a reload; paused while
    // dragging so a refetch can't clobber the in-flight order
    refetchInterval: activeId ? false : 10_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: boardKey });

  // persist collapsed set whenever it changes
  useEffect(() => {
    if (!boardId) return;
    localStorage.setItem(collapsedKey(boardId), JSON.stringify(collapsed));
  }, [boardId, collapsed]);

  const collapsedSet = useMemo(() => new Set(collapsed), [collapsed]);
  const toggleCollapsed = (lid: string) =>
    setCollapsed((prev) =>
      prev.includes(lid) ? prev.filter((id) => id !== lid) : [...prev, lid],
    );

  /* ---- mutations ---- */
  const renameBoard = useMutation({
    mutationFn: (title: string) => api.boards.update(boardId!, { title }),
    onSuccess: invalidate,
  });
  const addList = useMutation({
    mutationFn: (title: string) => api.lists.create(boardId!, { title }),
    onSuccess: invalidate,
  });
  const renameList = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      api.lists.update(id, { title }),
    onSuccess: invalidate,
  });
  const colorList = useMutation({
    mutationFn: ({ id, color }: { id: string; color: LabelColor | null }) =>
      api.lists.update(id, { color }),
    onSuccess: invalidate,
  });
  const removeList = useMutation({
    mutationFn: (id: string) => api.lists.delete(id),
    onSuccess: invalidate,
  });
  const archiveListCards = useMutation({
    mutationFn: (id: string) => api.lists.archiveCards(id),
    onSuccess: (r) => {
      toast(`Archived ${r.count} ${r.count === 1 ? "card" : "cards"}`);
      invalidate();
    },
    onError: (e: Error) => toast(e.message),
  });
  // reorder a list's cards in one go by rewriting their positions
  const sortListCards = useMutation({
    mutationFn: async ({ listId, by }: { listId: string; by: CardSort }) => {
      const cards = sortCards(listsById[listId]?.cards ?? []);
      // stable sort, so ties keep their current order
      const sorted = [...cards].sort(CARD_SORTS[by].compare);
      if (sorted.every((c, i) => c.id === cards[i].id)) return 0;
      // renumber every card: an untouched card's old position could land
      // between the new ones
      await Promise.all(
        sorted.map((c, i) => api.cards.move(c.id, { listId, position: i + 1 })),
      );
      return sorted.length;
    },
    onSuccess: (n, { by }) =>
      toast(n ? `Sorted by ${CARD_SORTS[by].label.toLowerCase()}` : "Already in that order"),
    onError: (e: Error) => toast(e.message),
    onSettled: invalidate,
  });
  const addCard = useMutation({
    mutationFn: ({ listId, title, position }: { listId: string; title: string; position?: number }) =>
      api.cards.create(listId, { title, position }),
    onSuccess: invalidate,
  });
  // moves apply optimistic local ordering, so resync on failure too. A board
  // fetch already in flight would land with the pre-move board and snap the
  // card back until the save settles, so drop it.
  const cancelBoardFetch = () => qc.cancelQueries({ queryKey: boardKey });
  const moveCard = useMutation({
    mutationFn: ({ cardId, listId, position }: { cardId: string; listId: string; position: number }) =>
      api.cards.move(cardId, { listId, position }),
    onMutate: cancelBoardFetch,
    onSettled: invalidate,
  });
  const moveList = useMutation({
    mutationFn: ({ id, position }: { id: string; position: number }) =>
      api.lists.reorder(id, { position }),
    onMutate: cancelBoardFetch,
    onSettled: invalidate,
  });

  const applyBoard = useCallback((b: BoardDetail) => {
    const ls = sortLists(b.lists);
    setListOrder(ls.map((l) => l.id));
    const map: Record<string, string[]> = {};
    for (const l of ls) map[l.id] = sortCards(l.cards).map((c) => c.id);
    setCardsByList(map);
  }, []);

  // Sync local ordering whenever the server board changes. Applied straight
  // away, even while someone is typing: the inline insert composer is anchored
  // to a card id (not an index), so new or moved cards can't remount it, and
  // holding the update made the board look frozen (and hid the user's own new
  // cards) for as long as focus sat in any field.
  useEffect(() => {
    if (!board) return;
    // mid-drag the settle-time invalidate resyncs us, so just skip (stashing
    // would let a pre-move board replay over the drop later)
    if (draggingRef.current) return;
    applyBoard(board);
  }, [board, applyBoard]);

  const faceFields = useMemo(
    () =>
      (board?.fields ?? [])
        .filter((f) => f.showOnCard)
        .sort((a, b) => a.position - b.position),
    [board],
  );

  // L on a hovered (or keyboard-focused) card opens its label picker in place,
  // so labelling doesn't need the card modal at all.
  const canEditBoard = !!board && board.role !== "viewer";
  useEffect(() => {
    if (!canEditBoard) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "l" && e.key !== "L") return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      if (isTypingTarget(document.activeElement)) return;
      // not while a modal (card, settings, …) is open over the board
      if (document.querySelector('[aria-modal="true"]')) return;
      const focused = document.activeElement?.closest<HTMLElement>("[data-card-id]");
      const hovered = Array.from(
        document.querySelectorAll<HTMLElement>("[data-card-id]:hover"),
      ).pop();
      // the pointer wins: closing a card modal leaves focus on that card's
      // link, which would otherwise hijack L wherever the mouse is
      const el = hovered ?? focused;
      if (!el) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      setLabelPicker({
        cardId: el.dataset.cardId!,
        left: Math.max(8, Math.min(r.left, window.innerWidth - PICKER_WIDTH - 8)),
        top: Math.max(8, Math.min(r.bottom + 4, window.innerHeight - PICKER_MAX_HEIGHT)),
      });
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [canEditBoard]);

  // the picker is pinned to the viewport, so drop it on outside click or scroll
  useEffect(() => {
    if (!labelPicker) return;
    const close = () => setLabelPicker(null);
    function onDown(e: MouseEvent) {
      if (!labelPickerRef.current?.contains(e.target as Node)) close();
    }
    function onScroll(e: Event) {
      if (!labelPickerRef.current?.contains(e.target as Node)) close();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [labelPicker]);

  const listsById = useMemo(() => {
    const m: Record<string, BoardDetail["lists"][number]> = {};
    for (const l of board?.lists ?? []) m[l.id] = l;
    return m;
  }, [board]);
  const cardsById = useMemo(() => {
    const m: Record<string, BoardCard> = {};
    for (const l of board?.lists ?? []) for (const c of l.cards) m[c.id] = c;
    return m;
  }, [board]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  function findListOfCard(cardId: string): string | undefined {
    for (const [lid, ids] of Object.entries(cardsByList)) {
      if (ids.includes(cardId)) return lid;
    }
    return undefined;
  }

  function listIdFromOver(overId: string): string | undefined {
    if (overId.startsWith(CONTAINER)) return overId.slice(CONTAINER.length);
    if (cardsById[overId]) return findListOfCard(overId);
    if (cardsByList[overId]) return overId; // hovering a list column
    return undefined;
  }

  /* ---- native file drops (attach to card / create card on list) ---- */

  async function attachFilesToCard(cardId: string, files: FileList) {
    try {
      for (const file of Array.from(files)) {
        await api.attachments.upload(cardId, file);
      }
    } catch (e) {
      toast((e as Error).message);
    }
    invalidate();
    qc.invalidateQueries({ queryKey: ["card", cardId] });
  }

  async function dropFilesOnList(
    listId: string,
    files: FileList,
    typedTitle?: string,
  ) {
    const dropped = Array.from(files);
    if (dropped.length === 0) return;
    try {
      // title defaults to the first file's name; unless the user already
      // typed one, the modal opens with the title in edit mode so the
      // filename can be replaced immediately
      const card = await api.cards.create(listId, {
        title: (typedTitle || dropped[0].name).slice(0, CARD_TITLE_MAX_LENGTH),
      });
      navigate(`/boards/${boardId}/cards/${card.id}`, {
        state: { editTitle: !typedTitle },
      });
      await attachFilesToCard(card.id, files);
    } catch (e) {
      toast((e as Error).message);
      invalidate();
    }
  }

  function onDragStart(e: DragStartEvent) {
    draggingRef.current = true;
    setActiveId(String(e.active.id));
    setActiveType((e.active.data.current?.type as "card" | "list") ?? null);
  }

  // A drag that ends without saving a move must put the board back itself:
  // onDragOver may have shuffled the card into another list locally, and a
  // refetch that landed mid-drag was skipped by the sync effect. Refetching
  // alone doesn't help, since unchanged data keeps the same reference and
  // never re-runs that effect, so re-apply the latest cached board directly.
  function restoreServerOrder() {
    const latest = qc.getQueryData<BoardDetail>(boardKey);
    if (latest) applyBoard(latest);
  }

  function onDragCancel() {
    draggingRef.current = false;
    setActiveId(null);
    setActiveType(null);
    restoreServerOrder();
    invalidate();
  }

  function onDragOver(e: DragOverEvent) {
    const { active, over } = e;
    if (!over || active.data.current?.type !== "card") return;
    const activeCardId = String(active.id);
    const overId = String(over.id);
    const toList = listIdFromOver(overId);
    if (!toList) return;
    setCardsByList((prev) => {
      // find the source list in prev, not this render's state, so two moves
      // queued before a re-render can't leave the card in both lists
      const fromList = Object.keys(prev).find((lid) => prev[lid].includes(activeCardId));
      if (!fromList || fromList === toList) return prev;
      const fromIds = prev[fromList].filter((id) => id !== activeCardId);
      const toIds = [...prev[toList]];
      let insertAt = toIds.length;
      if (!overId.startsWith(CONTAINER) && cardsById[overId]) {
        const idx = toIds.indexOf(overId);
        if (idx >= 0) insertAt = idx;
      }
      toIds.splice(insertAt, 0, activeCardId);
      return { ...prev, [fromList]: fromIds, [toList]: toIds };
    });
  }

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    const type = active.data.current?.type;
    draggingRef.current = false;
    setActiveId(null);
    setActiveType(null);
    if (!over) {
      restoreServerOrder();
      return;
    }
    const activeIdStr = String(active.id);
    const overId = String(over.id);

    if (type === "list") {
      const overListId = listIdFromOver(overId);
      const oldIdx = listOrder.indexOf(activeIdStr);
      const newIdx = overListId ? listOrder.indexOf(overListId) : -1;
      if (overListId === activeIdStr || oldIdx < 0 || newIdx < 0) {
        restoreServerOrder();
        return;
      }
      const newOrder = arrayMove(listOrder, oldIdx, newIdx);
      setListOrder(newOrder);
      const i = newOrder.indexOf(activeIdStr);
      const prev = i > 0 ? listsById[newOrder[i - 1]]?.position ?? null : null;
      const next =
        i < newOrder.length - 1 ? listsById[newOrder[i + 1]]?.position ?? null : null;
      moveList.mutate({ id: activeIdStr, position: midpoint(prev, next) });
      return;
    }

    if (type === "card") {
      const listId = findListOfCard(activeIdStr);
      if (!listId) {
        restoreServerOrder();
        return;
      }
      let order = cardsByList[listId];
      // same-list reorder finalised here (cross-list handled in onDragOver)
      if (cardsById[overId] && order.includes(overId) && overId !== activeIdStr) {
        const oldIdx = order.indexOf(activeIdStr);
        const newIdx = order.indexOf(overId);
        if (oldIdx !== newIdx) {
          order = arrayMove(order, oldIdx, newIdx);
          setCardsByList((prev) => ({ ...prev, [listId]: order }));
        }
      }
      const i = order.indexOf(activeIdStr);
      const prev = i > 0 ? cardsById[order[i - 1]]?.position ?? null : null;
      const next =
        i < order.length - 1 ? cardsById[order[i + 1]]?.position ?? null : null;
      moveCard.mutate({ cardId: activeIdStr, listId, position: midpoint(prev, next) });
    }
  }

  if (isLoading) return <div className="p-6 text-slate-500">Loading board…</div>;
  if (!board) return <div className="p-6 text-slate-500">Board not found.</div>;

  // the last poll failed (a 401 bounces to /login instead, so this is the
  // network or server); say so rather than leave a stale board looking live
  const offline = errorUpdatedAt > dataUpdatedAt;
  const canEdit = board.role !== "viewer";
  const isOwner = board.role === "owner";
  const q = query.trim().toLowerCase();
  const hasStructured = filtersActive(filters);
  // any active filter (text or structured) hides cards
  const filtering = !!q || hasStructured;
  // dragging is disabled while filtering (hidden cards would break ordering)
  const canDrag = canEdit && !filtering;
  // a card is shown only if it passes both the text query and the structured filters
  const cardShown = (card: BoardCard): boolean =>
    (!q || cardMatches(card, q, commentIndex?.[card.id])) &&
    (!hasStructured || cardPassesFilters(card, filters, me?.id ?? null));

  // render order (fall back to server data before the first sync tick)
  const orderedListIds = listOrder.length
    ? listOrder
    : sortLists(board.lists).map((l) => l.id);
  const cardIdsOf = (lid: string): string[] => {
    const ids =
      cardsByList[lid] ?? sortCards(listsById[lid]?.cards ?? []).map((c) => c.id);
    if (!filtering) return ids;
    return ids.filter((id) => cardsById[id] && cardShown(cardsById[id]));
  };
  const matchCount = filtering
    ? orderedListIds.reduce((n, lid) => n + cardIdsOf(lid).length, 0)
    : 0;

  const activeCard =
    activeId && activeType === "card" ? cardsById[activeId] : null;
  const activeList =
    activeId && activeType === "list" ? listsById[activeId] : null;

  return (
    // the board background spans the header too; the header frosts over it
    <div className="flex h-full flex-col" style={backgroundStyle(board.background)}>
      <div className="surface-glass border-b border-slate-200 px-4 py-3">
        <Link to="/boards" className="text-sm text-blue-600 hover:underline">
          ← All boards
        </Link>
        <div className="mt-1 flex items-start justify-between gap-3">
          <h1 className="flex min-w-0 items-center gap-1.5 text-xl font-semibold">
            <span className="shrink-0">
              <BoardSwitcher currentBoardId={board.id} />
            </span>
            {isOwner ? (
              <EditableText
                value={board.title}
                onCommit={(v) => renameBoard.mutate(v)}
                displayClassName="font-semibold text-slate-900"
                ariaLabel="Board title"
              />
            ) : (
              <span className="text-slate-900">{board.title}</span>
            )}
          </h1>
          <div className="flex shrink-0 items-center gap-2">
            <div className="relative">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setSearchEngaged(true)}
                onKeyDown={(e) => e.key === "Escape" && (e.target as HTMLInputElement).blur()}
                placeholder="Search cards…  /"
                aria-label="Search cards"
                className="w-44 rounded border border-slate-300 bg-[var(--surface)] px-2 py-1 text-sm outline-none focus:border-blue-400"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded px-1 text-slate-400 hover:text-slate-700"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <BoardFilters
              boardId={board.id}
              labels={board.labels}
              fields={board.fields ?? []}
              me={me}
              filters={filters}
              onChange={setFilters}
            />
            {filtering && (
              <span className="text-xs text-slate-500">
                {matchCount} {matchCount === 1 ? "match" : "matches"}
                {canEdit && view === "board" && <span className="text-slate-400"> · drag off</span>}
              </span>
            )}
            <div
              role="group"
              aria-label="View"
              className="flex overflow-hidden rounded bg-slate-100 text-slate-700"
            >
              {(
                [
                  ["board", Kanban, "Board view"],
                  ["table", Table2, "Table view"],
                ] as const
              ).map(([v, Icon, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  aria-pressed={view === v}
                  title={label}
                  aria-label={label}
                  className={`px-2 py-1.5 ${
                    view === v ? "bg-slate-300 text-slate-900" : "hover:bg-slate-200"
                  }`}
                >
                  <Icon size={16} />
                </button>
              ))}
            </div>
            {offline && (
              <span
                role="status"
                title={`Last synced ${new Date(dataUpdatedAt).toLocaleTimeString()}`}
                className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
              >
                Can't reach the server, retrying…
              </span>
            )}
            {board.role !== "owner" && (
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs capitalize text-slate-500">
                {board.role}
              </span>
            )}
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="rounded bg-slate-100 px-3 py-1 text-sm text-slate-700 hover:bg-slate-200"
            >
              {isOwner ? "Share" : "Members"}
            </button>
            <button
              type="button"
              onClick={() => setActivityOpen(true)}
              title="Board activity"
              aria-label="Board activity"
              className="rounded bg-slate-100 px-2 py-1.5 text-slate-700 hover:bg-slate-200"
            >
              <History size={16} />
            </button>
            <button
              type="button"
              onClick={() => setArchiveOpen(true)}
              title="Archived cards"
              aria-label="Archived cards"
              className="rounded bg-slate-100 px-2 py-1.5 text-slate-700 hover:bg-slate-200"
            >
              <Archive size={16} />
            </button>
            {canEdit && (
              <button
                type="button"
                onClick={() => setFieldsOpen(true)}
                title="Custom fields"
                aria-label="Custom fields"
                className="rounded bg-slate-100 px-2 py-1.5 text-slate-700 hover:bg-slate-200"
              >
                <Settings2 size={16} />
              </button>
            )}
            {isOwner && (
              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                title="Board settings"
                aria-label="Board settings"
                className="rounded bg-slate-100 px-2 py-1.5 text-slate-700 hover:bg-slate-200"
              >
                <Settings size={16} />
              </button>
            )}
          </div>
        </div>
        {board.description && (
          <p className="text-sm text-slate-500">{board.description}</p>
        )}
      </div>

      {view === "table" ? (
        <TableView board={board} cardShown={cardShown} canEdit={canEdit} me={me ?? null} />
      ) : (
      <CardFaceFieldsContext.Provider value={faceFields}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        {/* preventDefault keeps a missed file drop from navigating the page */}
        <div
          className="flex flex-1 items-start gap-4 overflow-x-auto p-4"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
        >
          <SortableContext items={orderedListIds} strategy={horizontalListSortingStrategy}>
            {orderedListIds.map((lid) => {
              const list = listsById[lid];
              if (!list) return null;
              return (
                <ListColumn
                  key={lid}
                  boardId={board.id}
                  list={list}
                  cardIds={cardIdsOf(lid)}
                  cardsById={cardsById}
                  canEdit={canEdit}
                  canDrag={canDrag}
                  showPreview={board.cardPreviews ?? false}
                  filtering={filtering}
                  dragging={!!activeId}
                  collapsed={collapsedSet.has(lid)}
                  onToggleCollapse={() => toggleCollapsed(lid)}
                  onRenameList={(title) => renameList.mutate({ id: lid, title })}
                  onSetColor={(color) => colorList.mutate({ id: lid, color })}
                  onRemoveList={() => removeList.mutate(lid)}
                  moveTargets={orderedListIds
                    .filter((id) => id !== lid && listsById[id])
                    .map((id) => ({ id, title: listsById[id].title }))}
                  onArchiveAll={() => archiveListCards.mutate(lid)}
                  onSortCards={(by) => sortListCards.mutate({ listId: lid, by })}
                  onAddCard={(title, position) =>
                    addCard.mutate({ listId: lid, title, position })
                  }
                  onAttachFiles={attachFilesToCard}
                  onDropFilesOnList={(files, typedTitle) =>
                    dropFilesOnList(lid, files, typedTitle)
                  }
                />
              );
            })}
          </SortableContext>

          {canEdit && <AddList onAdd={(title) => addList.mutate(title)} />}
        </div>

        <DragOverlay>
          {activeCard ? (
            <div className="w-64 rotate-2">
              <CardFace
                card={activeCard}
                boardId={board.id}
                showPreview={board.cardPreviews ?? false}
                overlay
              />
            </div>
          ) : activeList ? (
            <div
              className={`w-72 rounded-lg p-3 opacity-90 shadow-lg ${
                activeList.color ? "list-tint" : "bg-slate-100"
              }`}
              style={activeList.color ? listTint(activeList.color) : undefined}
            >
              <div className="font-medium text-slate-800">{activeList.title}</div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      </CardFaceFieldsContext.Provider>
      )}

      {shareOpen && (
        <ShareBoard
          boardId={board.id}
          isOwner={isOwner}
          currentOrgId={board.orgId}
          onClose={() => setShareOpen(false)}
        />
      )}

      {settingsOpen && (
        <BoardSettings board={board} onClose={() => setSettingsOpen(false)} />
      )}

      {archiveOpen && (
        <ArchivedCards
          boardId={board.id}
          canEdit={canEdit}
          onClose={() => setArchiveOpen(false)}
        />
      )}

      {fieldsOpen && (
        <BoardFieldsDialog boardId={board.id} onClose={() => setFieldsOpen(false)} />
      )}

      {activityOpen && (
        <BoardActivity
          boardId={board.id}
          onClose={() => setActivityOpen(false)}
        />
      )}

      {labelPicker && cardsById[labelPicker.cardId] && (
        <div
          ref={labelPickerRef}
          className="fixed z-40"
          style={{ top: labelPicker.top, left: labelPicker.left }}
        >
          <LabelPicker
            boardId={board.id}
            cardId={labelPicker.cardId}
            assigned={cardsById[labelPicker.cardId].labels}
            onClose={() => setLabelPicker(null)}
          />
        </div>
      )}

      {/* Card detail modal (nested route) */}
      <Outlet />
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/** Feeds the list's color into the .list-tint CSS class (see index.css),
 *  which handles the translucent fill and its hover state. */
function listTint(color: string): React.CSSProperties {
  return { "--tint": `var(--lc-${color}-bg)` } as React.CSSProperties;
}

interface ListColumnProps {
  boardId: string;
  list: BoardDetail["lists"][number];
  cardIds: string[];
  cardsById: Record<string, BoardCard>;
  canEdit: boolean;
  canDrag: boolean;
  showPreview: boolean;
  filtering: boolean;
  dragging: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onRenameList: (title: string) => void;
  onSetColor: (color: LabelColor | null) => void;
  onRemoveList: () => void;
  /** Other lists on this board — where "Move all cards" can send them. */
  moveTargets: { id: string; title: string }[];
  onArchiveAll: () => void;
  onSortCards: (by: CardSort) => void;
  onAddCard: (title: string, position?: number) => void;
  onAttachFiles: (cardId: string, files: FileList) => void;
  onDropFilesOnList: (files: FileList, typedTitle?: string) => void;
}

function ListColumn({
  boardId,
  list,
  cardIds,
  cardsById,
  canEdit,
  canDrag,
  showPreview,
  filtering,
  dragging,
  collapsed,
  onToggleCollapse,
  onRenameList,
  onSetColor,
  onRemoveList,
  moveTargets,
  onArchiveAll,
  onSortCards,
  onAddCard,
  onAttachFiles,
  onDropFilesOnList,
}: ListColumnProps) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } =
    useSortable({
      id: list.id,
      data: { type: "list" },
      // collapsed lists can't be reordered or drop-targeted (disabled removes
      // the node as both draggable and droppable); expand to move them
      disabled: !canDrag || collapsed,
    });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: CONTAINER + list.id,
  });
  const fileDrop = useFileDrop(onDropFilesOnList, canEdit);
  // id of the card whose gap above holds the open inline composer (null =
  // none). Anchored by id, not index, so a card arriving above it in a board
  // refresh doesn't shift the composer into another card's slot and remount it.
  const [insertBefore, setInsertBefore] = useState<string | null>(null);
  // the anchor card can leave this list (moved/archived by someone else); keep
  // the composer open by parking it at the end of the list instead
  const anchorGone = insertBefore !== null && !cardIds.includes(insertBefore);
  const [moveAllOpen, setMoveAllOpen] = useState(false);
  // the bulk actions work on the whole column, not just what a filter leaves
  // visible, so they count and label themselves off the real card total
  const totalCards = list.cards.length;
  // no insert affordance while filtering (the new card would likely be hidden
  // by the active filter right away) or mid-drag (visual noise)
  const showGaps = canEdit && !filtering && !dragging;

  function insertCard(title: string, beforeId: string) {
    const at = cardIds.indexOf(beforeId);
    const index = at < 0 ? cardIds.length : at;
    const prev = cardsById[cardIds[index - 1]]?.position ?? null;
    const next = cardsById[cardIds[index]]?.position ?? null;
    onAddCard(title, midpoint(prev, next));
  }
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(list.color ? listTint(list.color) : {}),
  };

  // Collapsed: a slim vertical bar. The whole bar is the expand button; no
  // card rendering, no AddCard, and (drag disabled above) no card drops.
  if (collapsed) {
    return (
      <button
        ref={setNodeRef}
        type="button"
        onClick={onToggleCollapse}
        aria-label={`Expand list ${list.title}`}
        title={list.title}
        style={style}
        className={`flex w-10 shrink-0 flex-col items-center gap-2 rounded-lg py-3 transition-colors ${
          list.color ? "list-tint" : "bg-slate-100/85 hover:bg-slate-100"
        }`}
      >
        <span className="text-xs text-slate-400">{cardIds.length}</span>
        <span
          className="font-medium text-slate-800"
          style={{ writingMode: "vertical-rl" }}
        >
          {list.title}
        </span>
      </button>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...fileDrop.props}
      className={`flex max-h-full w-72 shrink-0 flex-col rounded-lg p-3 transition-colors ${
        list.color ? "list-tint" : "bg-slate-100/85 hover:bg-slate-100"
      } ${isDragging ? "opacity-40" : ""} ${
        fileDrop.isOver ? "ring-2 ring-blue-400" : ""
      }`}
    >
      <div className="mb-2 flex shrink-0 items-center gap-1">
        {canDrag && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            title="Drag to reorder list"
            aria-label="Reorder list"
            className="cursor-grab rounded px-0.5 py-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 active:cursor-grabbing"
          >
            <GripVertical size={14} />
          </button>
        )}
        <div className="flex-1">
          {canEdit ? (
            <EditableText
              value={list.title}
              onCommit={onRenameList}
              displayClassName="font-medium text-slate-800"
              ariaLabel="List title"
            />
          ) : (
            <span className="font-medium text-slate-800">{list.title}</span>
          )}
        </div>
        <span className="text-xs text-slate-400" aria-label="Card count">
          {cardIds.length}
        </span>
        {canEdit && (
          <OverflowMenu
            ariaLabel="List actions"
            triggerClassName="rounded px-1 py-1 leading-none text-slate-400 hover:bg-slate-200 hover:text-slate-600"
          >
            {(close) => (
              <>
                <div className="w-56 px-3 py-2">
                  <div className="mb-1.5 text-xs text-slate-500">List color</div>
                  <ColorSwatches
                    value={list.color}
                    onChange={onSetColor}
                    allowNone
                  />
                </div>
                <div className="my-1 border-t border-slate-100" />
                <button
                  type="button"
                  onClick={onToggleCollapse}
                  className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100"
                >
                  Collapse list
                </button>
                {totalCards > 0 && (
                  <>
                    <div className="my-1 border-t border-slate-100" />
                    <button
                      type="button"
                      disabled={moveTargets.length === 0}
                      onClick={() => {
                        close();
                        setMoveAllOpen(true);
                      }}
                      title={
                        moveTargets.length === 0
                          ? "This board has no other list to move them to"
                          : undefined
                      }
                      className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
                    >
                      Move all cards…
                    </button>
                    {totalCards > 1 && (
                      <>
                        <div className="px-3 pb-0.5 pt-1.5 text-xs text-slate-500">
                          Sort cards by
                        </div>
                        {(Object.keys(CARD_SORTS) as CardSort[]).map((by) => (
                          <button
                            key={by}
                            type="button"
                            onClick={() => {
                              close();
                              onSortCards(by);
                            }}
                            className="block w-full px-3 py-1.5 pl-5 text-left text-sm text-slate-700 hover:bg-slate-100"
                          >
                            {CARD_SORTS[by].label}
                          </button>
                        ))}
                        <div className="my-1 border-t border-slate-100" />
                      </>
                    )}
                    <ConfirmButton
                      onConfirm={() => {
                        close();
                        onArchiveAll();
                      }}
                      title="Archive every card in this list"
                      className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100"
                      confirmClassName="block w-full bg-slate-100 px-3 py-1.5 text-left text-sm font-medium text-slate-800"
                      confirmLabel={`Click again to archive ${totalCards}`}
                    >
                      Archive all cards
                    </ConfirmButton>
                  </>
                )}
                <div className="my-1 border-t border-slate-100" />
                <ConfirmButton
                  onConfirm={onRemoveList}
                  title="Delete list"
                  className="block w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                  confirmClassName="block w-full px-3 py-1.5 text-left text-sm font-medium text-red-700 bg-red-50"
                  confirmLabel="Click again to delete"
                >
                  Delete list
                </ConfirmButton>
              </>
            )}
          </OverflowMenu>
        )}
      </div>

      {/* the cards area scrolls internally once the column hits the viewport
          cap; header and AddCard stay pinned. Negative margins + matching
          padding give card shadows/rings and the scrollbar room without
          shifting the cards. */}
      <div
        ref={setDropRef}
        className="themed-scroll -ml-1 -mr-2 min-h-0 overflow-y-auto pl-1 pr-2"
      >
        <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
          {/* gaps between cards stand in for space-y so each one can host the
              hover-to-insert affordance without doubling the spacing */}
          <ul className="min-h-[2rem]">
            {cardIds.map((id, i) =>
              cardsById[id] ? (
                <Fragment key={id}>
                  {insertBefore === id ? (
                    <li className="py-1">
                      <CardComposer
                        onSubmit={(title) => insertCard(title, id)}
                        onClose={() => setInsertBefore(null)}
                        closeOnSubmit
                      />
                    </li>
                  ) : (
                    <InsertGap
                      visible={showGaps}
                      first={i === 0}
                      onClick={() => setInsertBefore(id)}
                    />
                  )}
                  <SortableCard
                    card={cardsById[id]}
                    boardId={boardId}
                    canDrag={canDrag}
                    canEdit={canEdit}
                    showPreview={showPreview}
                    onDropFiles={(files) => onAttachFiles(id, files)}
                  />
                </Fragment>
              ) : null,
            )}
            {anchorGone && (
              <li key="orphaned-composer" className="py-1">
                <CardComposer
                  onSubmit={(title) => insertCard(title, insertBefore)}
                  onClose={() => setInsertBefore(null)}
                  closeOnSubmit
                />
              </li>
            )}
            {cardIds.length === 0 && !anchorGone && (
              <li
                className={`rounded border border-dashed py-3 text-center text-xs text-slate-400 ${
                  isOver && canDrag ? "border-blue-400 bg-blue-50" : "border-slate-300"
                }`}
              >
                {filtering ? "No matches" : canEdit ? "No cards — drop here" : "No cards"}
              </li>
            )}
          </ul>
        </SortableContext>
      </div>

      {canEdit && (
        <div className="shrink-0">
          <AddCard onAdd={onAddCard} onPasteFiles={onDropFilesOnList} />
        </div>
      )}

      {moveAllOpen && (
        <MoveListCardsDialog
          boardId={boardId}
          listId={list.id}
          listTitle={list.title}
          cardCount={totalCards}
          targets={moveTargets}
          onClose={() => setMoveAllOpen(false)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/** Slim spacer between two cards; hovering reveals a [+] to insert a card in
 *  place. Doubles as the list's inter-card spacing (replaces space-y), except
 *  above the first card where it stays zero-height so the list top doesn't
 *  shift — only the hover target extends there. */
function InsertGap({
  visible,
  first,
  onClick,
}: {
  visible: boolean;
  first?: boolean;
  onClick: () => void;
}) {
  const height = first ? "h-0" : "h-2";
  if (!visible) return <li aria-hidden className={height} />;
  return (
    <li className={`relative ${height}`}>
      <button
        type="button"
        onClick={onClick}
        title="Insert a card here"
        aria-label="Insert a card here"
        className="absolute inset-x-0 -inset-y-1 z-10 flex items-center opacity-0 transition-opacity hover:opacity-100 focus-visible:opacity-100"
      >
        <span className="h-px flex-1 bg-blue-500" />
        <span className="mx-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-white">
          <Plus size={12} />
        </span>
        <span className="h-px flex-1 bg-blue-500" />
      </button>
    </li>
  );
}

/* -------------------------------------------------------------------------- */

function SortableCard({
  card,
  boardId,
  canDrag,
  canEdit,
  showPreview,
  onDropFiles,
}: {
  card: BoardCard;
  boardId: string;
  canDrag: boolean;
  canEdit: boolean;
  showPreview: boolean;
  onDropFiles: (files: FileList) => void;
}) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } =
    useSortable({
      id: card.id,
      data: { type: "card", listId: card.listId },
      disabled: !canDrag,
    });
  const fileDrop = useFileDrop(onDropFiles, canEdit);
  const style = { transform: CSS.Translate.toString(transform), transition };
  return (
    <li
      ref={setNodeRef}
      style={style}
      data-card-id={card.id}
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
      {...fileDrop.props}
      className={isDragging ? "opacity-40" : ""}
    >
      <CardFace
        card={card}
        boardId={boardId}
        showPreview={showPreview}
        fileOver={fileDrop.isOver}
      />
    </li>
  );
}

/** The visual card face, shared by the sortable card and the drag overlay. */
function CardFace({
  card,
  boardId,
  overlay,
  showPreview,
  fileOver,
}: {
  card: BoardCard;
  boardId: string;
  overlay?: boolean;
  /** Show the card's first image attachment as a cover above its content. */
  showPreview?: boolean;
  /** Files are being dragged over this card (drop = attach). */
  fileOver?: boolean;
}) {
  const cover = card.coverColor ? labelColor(card.coverColor) : null;

  const body = (
    <>
      {showPreview && card.previewAttachmentId && (
        <div className="-mx-2 -mt-2 mb-2 overflow-hidden rounded-t">
          <img
            src={`/api/attachments/${card.previewAttachmentId}`}
            alt=""
            loading="lazy"
            className="h-24 w-full object-cover"
          />
        </div>
      )}
      {card.labels.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {card.labels.map((l) => (
            <LabelChip key={l.id} label={l} compact />
          ))}
        </div>
      )}
      <span
        className={`font-medium ${
          card.completed
            ? cover
              ? "line-through opacity-70"
              : "text-slate-500 line-through"
            : ""
        }`}
      >
        {card.title}
      </span>
      <CardBadges card={card} />
      <CardFieldPills card={card} />
      {card.assignees.length > 0 && (
        <div className="mt-1.5 flex flex-wrap justify-end gap-0.5">
          {card.assignees.map((a) => (
            <Avatar
              key={a.userId}
              userId={a.userId}
              name={a.displayName || a.username}
              title={`@${a.username}`}
            />
          ))}
        </div>
      )}
    </>
  );

  const className = `block rounded border p-2 text-sm transition ${
    cover ? `${cover.cover} ${cover.text}` : "border-slate-200 bg-[var(--surface)]"
  } ${overlay ? "cursor-grabbing shadow-lg" : "cursor-pointer hover:shadow"} ${
    fileOver ? "ring-2 ring-blue-400" : ""
  }`;
  // same-hue outline (25% of the text color) keeps a covered card visible
  // against a list tinted the same color
  const coverBorder = cover
    ? { borderColor: "color-mix(in srgb, currentColor 25%, transparent)" }
    : undefined;

  if (overlay)
    return (
      <div className={className} style={coverBorder}>
        {body}
      </div>
    );

  return (
    <Link
      to={`/boards/${boardId}/cards/${card.id}`}
      className={className}
      style={coverBorder}
    >
      {body}
    </Link>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Whether the current user has an unread notification pointing at this card.
 * Piggybacks on the bell's ["notifications"] query (same key + fn, so the
 * cache is shared and the bell's 30s poll keeps it fresh); `select` means a
 * card only re-renders when its own boolean flips, not on every poll.
 */
function useHasUnreadNotification(cardId: string): boolean {
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.notifications.list(),
    select: (items) => items.some((n) => !n.readAt && n.cardId === cardId),
  });
  return data ?? false;
}

/** Pills for the board's "show on card" fields that this card has a value for. */
function CardFieldPills({ card }: { card: BoardCard }) {
  const fields = useContext(CardFaceFieldsContext);
  const shown = fields.filter((f) => formatFieldValue(f, card.fieldValues?.[f.id]) !== "");
  if (shown.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1 text-xs">
      {shown.map((f) => {
        const value = card.fieldValues[f.id];
        const opt = f.type === "select" ? f.options.find((o) => o.id === value) : undefined;
        return (
          <span
            key={f.id}
            title={`${f.name}: ${formatFieldValue(f, value)}`}
            className={`inline-flex max-w-full items-center gap-1 truncate rounded px-1.5 py-0.5 ${
              opt ? labelColor(opt.color).chip : "bg-slate-100 text-slate-600"
            }`}
          >
            <span className="opacity-70">{f.name}:</span>
            <span className="truncate">{formatFieldValue(f, value)}</span>
          </span>
        );
      })}
    </div>
  );
}

function CardBadges({ card }: { card: BoardCard }) {
  const hasChecklist = card.checklistTotal > 0;
  const checklistDone =
    hasChecklist && card.checklistDone === card.checklistTotal;
  const overdue = !!card.dueDate && !card.completed && isOverdue(card.dueDate);
  const dueSoon =
    !!card.dueDate && !card.completed && !overdue && isDueSoon(card.dueDate);
  const hasAttachments = card.attachmentCount > 0;
  const hasUnread = useHasUnreadNotification(card.id);
  // An unread notification shows the bubble even on a comment-less card
  // (e.g. an "assigned" notification) so the dot always has somewhere to sit.
  const hasComments = card.commentCount > 0 || hasUnread;

  if (
    !card.dueDate &&
    !hasChecklist &&
    !card.completed &&
    !hasAttachments &&
    !hasComments
  )
    return null;

  // Covers now share the theme's tonality (pastel in light, deep in dark), so
  // the regular pill styles read fine on covered and uncovered cards alike.
  const neutral = "bg-slate-100 text-slate-600";
  const positive = "bg-green-100 text-green-700";
  const danger = "bg-red-100 text-red-700";
  const amber = "bg-amber-100 text-amber-800";

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
      {card.completed && (
        <span
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium ${positive}`}
        >
          <Check size={12} /> Done
        </span>
      )}
      {card.dueDate && (
        <span
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${
            overdue ? danger : dueSoon ? amber : neutral
          }`}
          title={overdue ? "Overdue" : dueSoon ? "Due soon" : "Due date"}
        >
          <Clock size={12} /> {formatDueShort(card.dueDate)}
        </span>
      )}
      {hasChecklist && (
        <span
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${
            checklistDone ? positive : neutral
          }`}
          title="Checklist items complete"
        >
          <CheckSquare size={12} /> {card.checklistDone}/{card.checklistTotal}
        </span>
      )}
      {hasAttachments && (
        <span
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${neutral}`}
          title="Attachments"
        >
          <Paperclip size={12} /> {card.attachmentCount}
        </span>
      )}
      {hasComments && (
        <span
          className={`relative inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${neutral}`}
          title={hasUnread ? "Comments (unread notification)" : "Comments"}
        >
          <MessageSquare size={12} />
          {card.commentCount > 0 && card.commentCount}
          {hasUnread && (
            <span
              aria-hidden
              className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-blue-600"
            />
          )}
        </span>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/** Inline title composer shared by the bottom "+ Add a card" flow and the
 *  between-cards insert gaps. */
function CardComposer({
  onSubmit,
  onClose,
  onPasteFiles,
  closeOnSubmit,
}: {
  onSubmit: (title: string) => void;
  onClose: () => void;
  /** Pasting files creates a card with them attached (appended at the end). */
  onPasteFiles?: (files: FileList, typedTitle?: string) => void;
  /** Close after submit instead of staying open for rapid entry. */
  closeOnSubmit?: boolean;
}) {
  const [title, setTitle] = useState("");

  function submit() {
    const t = title.trim();
    if (!t) {
      onClose();
      return;
    }
    onSubmit(t);
    setTitle("");
    if (closeOnSubmit) onClose();
  }

  return (
    <div>
      <textarea
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          } else if (e.key === "Escape") {
            onClose();
          }
        }}
        onBlur={() => {
          submit();
          onClose();
        }}
        onPaste={(e) => {
          if (!onPasteFiles) return;
          const files = clipboardFiles(e);
          if (!files) return;
          e.preventDefault();
          onPasteFiles(files, title.trim() || undefined);
          setTitle("");
          onClose();
        }}
        rows={2}
        maxLength={CARD_TITLE_MAX_LENGTH}
        placeholder="Card title"
        className="w-full resize-none rounded border border-slate-300 px-2 py-1 text-sm outline-none focus:border-blue-400"
      />
      <div className="mt-1 flex gap-2 text-xs">
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={submit}
          className="rounded bg-blue-600 px-2 py-1 text-white hover:bg-blue-700"
        >
          Add
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClose}
          className="text-slate-500 hover:underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function AddCard({
  onAdd,
  onPasteFiles,
}: {
  onAdd: (title: string) => void;
  /** Pasting files into the composer creates a card with them attached. */
  onPasteFiles: (files: FileList, typedTitle?: string) => void;
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 w-full rounded px-2 py-1 text-left text-sm text-slate-500 hover:bg-slate-200"
      >
        + Add a card
      </button>
    );
  }

  return (
    <div className="mt-2">
      <CardComposer
        onSubmit={onAdd}
        onClose={() => setOpen(false)}
        onPasteFiles={onPasteFiles}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function AddList({ onAdd }: { onAdd: (title: string) => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");

  function submit() {
    const t = title.trim();
    if (!t) {
      setOpen(false);
      return;
    }
    onAdd(t);
    setTitle("");
    setOpen(false);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-72 shrink-0 rounded-lg bg-slate-200/70 p-3 text-left text-sm text-slate-600 hover:bg-slate-200"
      >
        + Add a list
      </button>
    );
  }

  return (
    <div className="w-72 shrink-0 rounded-lg bg-slate-100 p-3">
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          } else if (e.key === "Escape") {
            setOpen(false);
            setTitle("");
          }
        }}
        onBlur={submit}
        placeholder="List title"
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm outline-none focus:border-blue-400"
      />
    </div>
  );
}
